import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { type Filter } from '@cubejs-client/core'
import { CubeReportPage } from 'cube/components/CubeReportFilterContainer'
import {
  CubeReportFilterRow,
  isValidCubeFilter,
  useCubeReportFilterableEntities,
} from 'cube/components/CubeReportFilterRow'
import { useCubeReportPageFiltersContext } from 'cube/contexts'
import { pluralize } from 'humanize-plus'
import { clone, equals, isEmpty } from 'ramda'

import { Button, ButtonGroup, Flex, Icon, Popover } from '@driftt/dds'
import { faFilter } from '@driftt/dds.icons/regular/faFilter'
import { faPlus } from '@driftt/dds.icons/regular/faPlus'

import { isNilOrEmpty } from 'utils/ramda'
import { trackEvent } from 'utils/tracking'

import {
  CubeFilterButtonIconContainer,
  CubeFilterButtonIndicator,
} from './CubeReportFilterContainer.styles'

const FILTER_PLACEHOLDER = {} as Filter

interface CubeReportFilterContainerProps {
  reportType: CubeReportPage
}

export const CubeReportFilterContainer = ({
  reportType,
}: CubeReportFilterContainerProps) => {
  const { pageFilters, onChangePageFilters } = useCubeReportPageFiltersContext()
  const [draftPageFilters, setDraftPageFilters] = useState<Filter[]>(
    isNilOrEmpty(pageFilters) ? [FILTER_PLACEHOLDER] : pageFilters
  )
  const { filterableEntitiesByReportType } =
    useCubeReportFilterableEntities(reportType)

  const sendFilterChangeTrackEvent = (validPageFilters: Filter[]) => {
    if (!isNilOrEmpty(validPageFilters)) {
      trackEvent('CUBE_REPORT_PAGE_FILTER_CHANGED', {
        filters: validPageFilters,
        pageName: window.location.pathname,
      })
    }
  }

  // Updates page filter context with valid filters
  const handleChangePageFilters = useCallback(
    (pageFilters: Filter[]) => {
      const validPageFilters = pageFilters.filter((filter) =>
        isValidCubeFilter(filter)
      )
      onChangePageFilters(validPageFilters)
      sendFilterChangeTrackEvent(validPageFilters)
      dispatchEvent(new Event('enter'))
    },
    [onChangePageFilters]
  )

  const updateFilters = useCallback(
    (newFilter: Filter, index: number) => {
      const newFilters = clone(draftPageFilters)
      newFilters.splice(index, 1, newFilter)
      setDraftPageFilters(newFilters)
    },
    [draftPageFilters, setDraftPageFilters]
  )

  const deleteFilter = useCallback(
    (index: number) => {
      const newFilters = clone(draftPageFilters)
      newFilters.splice(index, 1)
      if (isEmpty(newFilters)) {
        newFilters.push(clone(FILTER_PLACEHOLDER))
      }
      setDraftPageFilters(newFilters)
    },
    [draftPageFilters, setDraftPageFilters]
  )

  const addFilterRow = useCallback(() => {
    const newFilters = [...draftPageFilters, clone(FILTER_PLACEHOLDER)]
    setDraftPageFilters(newFilters)
  }, [draftPageFilters, setDraftPageFilters])

  const draftHasChanges = useMemo(() => {
    const draftChanged = !equals(draftPageFilters, pageFilters)
    const draftIsNotEmpty = draftPageFilters.find(
      (filter) => !isNilOrEmpty(filter)
    )
    const originalIsNotEmpty = pageFilters.find(
      (filter) => !isNilOrEmpty(filter)
    )
    return draftChanged && (draftIsNotEmpty || originalIsNotEmpty)
  }, [draftPageFilters, pageFilters])

  const handleCancelChanges = () => {
    const newFilters = isNilOrEmpty(pageFilters)
      ? [FILTER_PLACEHOLDER]
      : pageFilters
    setDraftPageFilters(newFilters)
  }

  useEffect(() => {
    setDraftPageFilters(
      isNilOrEmpty(pageFilters) ? [FILTER_PLACEHOLDER] : pageFilters
    )
  }, [pageFilters])

  return (
    <Flex
      rootProps={{
        id: `cube-report--filter-container`,
      }}
    >
      <Popover.Root>
        <Popover.Trigger>
          <Button
            variant="secondary"
            rootProps={{
              id: 'cube-report--view-filter-container-button',
            }}
          >
            <CubeFilterButtonIconContainer>
              {pageFilters.length > 0 && (
                <CubeFilterButtonIndicator variant="info" size="x-small" />
              )}
              <Icon icon={faFilter} />
            </CubeFilterButtonIconContainer>
            {pageFilters.length > 0 ? pageFilters.length : ''}{' '}
            {pluralize(pageFilters.length, 'Filter')}
          </Button>
        </Popover.Trigger>
        <Popover.Content align="start" hideCloseButton arrowSize="small">
          <Flex fullWidth direction="column">
            {draftPageFilters.map((filter, index) => (
              <CubeReportFilterRow
                key={`cube-page-filter-${index}`}
                filter={filter}
                onUpdateFilter={(newFilter: Filter) =>
                  updateFilters(newFilter, index)
                }
                onDeleteFilter={() => deleteFilter(index)}
                filterableEntities={filterableEntitiesByReportType}
              />
            ))}
          </Flex>
          <Flex fullWidth justifyContent="flex-start">
            <Button
              variant="tertiary"
              onClick={addFilterRow}
              rootProps={{
                id: 'cube-report--add-filter-button',
              }}
            >
              <Icon icon={faPlus} />
              Add filter
            </Button>
          </Flex>
          <ButtonGroup align="left">
            <Popover.Close asChild>
              <Button
                disabled={!draftHasChanges}
                onClick={() => handleChangePageFilters(draftPageFilters)}
                rootProps={{
                  id: 'cube-report--apply-filter-button',
                }}
              >
                Apply
              </Button>
            </Popover.Close>
            <Button
              variant="secondary"
              disabled={!draftHasChanges}
              onClick={handleCancelChanges}
              rootProps={{
                id: 'cube-report--cancel-filter-changes-button',
              }}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Popover.Content>
      </Popover.Root>
    </Flex>
  )
}
